import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { translate } from 'components/Translation';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import NumberSettingsCard from 'components/Card/NumberSettingsCard';
import ReloadPage from 'components/ReloadPage/ReloadPage';
import SimPin from 'assets/img/numbers/settings/sim-pin.svg';
import SimReplace from 'assets/img/numbers/settings/sim-replace.svg';
import QrCode from 'assets/img/numbers/settings/qr-code.svg';
import DataSim from 'assets/img/numbers/settings/data-sim.svg';
import SawaSim from 'assets/img/numbers/settings/sawa-sim.svg';
import { capitalize } from 'utils/converters/capitalize';
import CheckPinPukDialog from './subcomponents/CheckPinPukDialog';
import styles from 'assets/jss/material-dashboard-pro-react/views/settingsSubpageStyle';
import { makeStyles } from '@material-ui/core';
import { decode } from 'utils/crypto/crypto';
import ReplaceSimCardDialog from './subcomponents/ReplaceSimCardDialog';
import ReplaceESimCardDialog from './subcomponents/ReplaceESimCardDialog';
import { CustomerAccount } from 'utils/api/models/accounts/customerAccountResponse';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import NoSim from '@material-ui/icons/NoSim';
import DisconnectSimCardDialog from './subcomponents/DisconnectSimCardDialog';
import { ResourceInfo } from 'utils/api/models/products/customerProducts';
import { useLocation } from 'react-router';
import moment from 'moment';
import { ProductsService } from 'services/products/products';
import { finalize } from 'rxjs/operators';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import SimDialog from './subcomponents/SimDialog';
import { BillingType } from 'utils/api/models/accounts/singleAccount';
import { SimType } from 'utils/api/payloads/microApi';
import { TagManager } from 'commons/gtm/tagManager';
import { getLanguage } from 'utils/language/language';
import { upIdSelector, userIdSelector } from 'redux/modules/session/selectors';
import { useSelector } from 'react-redux';
import { AuthConsumer } from 'components/Auth/authContext';
import { remoteConfigSelector } from 'redux/modules/settings/remoteConfig/selectors';
import { isESimReplacementEnabled, isSIMManagementUnavailable } from 'utils/killswitch';
import simAlertSVG from 'assets/img/icons/simAlert.svg';
import { getSimMgmtLabels } from 'redux/modules/dynamicLabels/selectors';

// @ts-ignore
const useStyles = makeStyles(styles);

const MAX_NUMBER_OF_DATA_SIM_CARDS = 2;
const MAX_NUMBER_OF_SAWA_SIM_CARDS = 1;

const useQuery = () => {
	return new URLSearchParams(useLocation().search);
};

type dialogType =
	| 'checkPinPukDialog'
	| 'simCardDialog'
	| 'esimCardDialog'
	| 'disconnectSimCard'
	| 'esimQrCode'
	| 'dataSimDialog'
	| 'sawaSimDialog';
interface ISimManagementSubpageProps {
	customerAccount: CustomerAccount;
	match: {
		path: string;
		params: {
			phoneNumberEncoded: string;
			accountType: string;
			billingType: string;
			accountId: string;
			accountNumber: string;
		};
	};
	reload: () => void;
	accountName: string;
	isSimCardDisconnected: boolean;
	reloadCustomerProducts: () => void;
	resourceInfo: ResourceInfo | null;
}

const SimManagement = ({
	accountName,
	customerAccount,
	reload,
	match,
	isSimCardDisconnected,
	reloadCustomerProducts,
	resourceInfo,
}: ISimManagementSubpageProps) => {
	const [showDialog, setShowDialog] = useState<dialogType | null>(null);
	const { phoneNumberEncoded, accountType, billingType, accountId } = match.params;
	const [loading, setLoading] = useState<boolean>(false);
	const [isEsim, setIsEsim] = useState<boolean | undefined>();
	const accountDetails: string = `${capitalize(accountType)} - ${capitalize(billingType)}`;
	const phoneNumber: string = decode(phoneNumberEncoded);
	const classes = useStyles();
	const query = useQuery();
	const openDialog = query.get('open') as dialogType;
	const isPrepaid = billingType === BillingType.PREPAID;
	const isPostpaid = billingType === BillingType.POSTPAID;

	// kill switch to check if Bank Muscat are to be shown
	const remoteConfig = useSelector(remoteConfigSelector);
	const enableESimReplacement = isESimReplacementEnabled(remoteConfig);
	const enableSIManagement = !isSIMManagementUnavailable(remoteConfig);

	const accId = useSelector(upIdSelector);
	const userId = useSelector(userIdSelector);
	const simMgmtLabels = useSelector(getSimMgmtLabels);

	const fetchEsimData = useCallback(() => {
		setLoading(true);

		return ProductsService.getSimDetails({
			phoneNumber: phoneNumber,
		})
			.pipe(
				finalize(() => {
					setLoading(false);
				})
			)
			.subscribe(
				({ data }) => {
					setIsEsim(data?.eSIM);
				},
				() => {
					setIsEsim(false);
				}
			);
	}, [phoneNumber]);

	useEffect(() => {
		const subscription = fetchEsimData();
		return () => {
			subscription.unsubscribe();
		};
	}, [fetchEsimData]);

	useEffect(() => {
		if (openDialog) {
			setShowDialog(openDialog);
		}
	}, [openDialog]);

	const replaceSimCard = () => {
		if (customerAccount && enableSIManagement) {
			setShowDialog('simCardDialog');
		}
	};

	const replaceESimCard = () => {
		if ((!dateReplacedToday || !isEsim) && customerAccount && enableSIManagement) {
			setShowDialog('esimCardDialog');
		}
	};

	const checkPinPuk = () => {
		if (customerAccount) {
			setShowDialog('checkPinPukDialog');
		}
	};

	const addDataSim = () => {
		if (customerAccount && enableSIManagement) {
			setShowDialog('dataSimDialog');
		}
	};

	const addSawaSim = () => {
		if (customerAccount && enableSIManagement) {
			setShowDialog('sawaSimDialog');
		}
	};

	const disconnectSimCard = () => {
		if (customerAccount) {
			setShowDialog('disconnectSimCard');
		}
	};

	const hideDialog = () => {
		setShowDialog(null);
	};

	const dateReplacedToday = useMemo(() => {
		if (!!resourceInfo) {
			return moment(resourceInfo?.startDate).isSame(moment(), 'day');
		}
		return false;
	}, [resourceInfo]);

	const reloadAfterESimReplacement = () => {
		reloadCustomerProducts();
		fetchEsimData();
	};

	React.useEffect(() => {
		TagManager.dataLayer({
			dataLayer: {
				event: 'My Services',
				step: `My Numbers: Sim Management`,
				accountID: accId,
				billingID: match.params.accountNumber,
				accountNumber: phoneNumber,
				userId: userId,
				language: getLanguage(),
			},
		});
	}, []);

	return (
		<GridContainer justify="flex-start">
			<BreadcrumbsItem to={match.path}>{`${phoneNumber} (${accountName}) - ${translate(
				'path.sim.management'
			)}`}</BreadcrumbsItem>
			<ConditionalRender
				show={!enableSIManagement}
				onTrue={() => (
					<GridItem xs={12}>
						<div className={classes.simAlert}>
							<img className={classes.icon} src={simAlertSVG} alt="" role="presentation" />
							<div>
								<div className={classes.simAlertTitle}>{simMgmtLabels.alertTitle}</div>
								{simMgmtLabels.alertDescription}
							</div>
						</div>
					</GridItem>
				)}
			/>
			<ConditionalRender
				show={!isPrepaid}
				onTrue={() => (
					<GridItem xs={12} lg={4}>
						<NumberSettingsCard
							text={
								isSimCardDisconnected
									? translate('number.disconnection.active')
									: translate('numbers.settings.sim.temporary.disconnection')
							}
							image={<NoSim />}
							onClick={disconnectSimCard}
							error={isSimCardDisconnected}
							notAvailable={!customerAccount}
						/>
						<AuthConsumer>
							{({ user }) => (
								<DisconnectSimCardDialog
									isOpen={showDialog === 'disconnectSimCard'}
									hideDialog={hideDialog}
									isDisconnected={isSimCardDisconnected}
									customerAccount={customerAccount}
									phoneNumber={phoneNumber}
									accountId={accountId}
									fetchCustomerProducts={reloadCustomerProducts}
									msisdn={user.contactNumber1}
								/>
							)}
						</AuthConsumer>
					</GridItem>
				)}
			/>
			<GridItem xs={12} lg={4}>
				<NumberSettingsCard
					text={translate('numbers.settings.replace.sim.card')}
					image={SimReplace}
					onClick={replaceSimCard}
					notAvailable={!customerAccount}
					simManagementUnavailable={!enableSIManagement}
				/>
				<AuthConsumer>
					{({ user }) => (
						<ReplaceSimCardDialog
							isOpen={showDialog === 'simCardDialog'}
							hideDialog={hideDialog}
							showDialog={replaceSimCard}
							phoneNumber={phoneNumber}
							accountDetails={accountDetails}
							billingType={billingType}
							customerAccount={customerAccount}
							resourceInfo={resourceInfo}
							msisdn={user.contactNumber1}
						/>
					)}
				</AuthConsumer>
			</GridItem>

			{enableESimReplacement && (
				<GridItem xs={12} lg={4}>
					<NumberSettingsCard
						text={translate('numbers.settings.replace.esim.card')}
						image={QrCode}
						onClick={replaceESimCard}
						notAvailable={!customerAccount || (dateReplacedToday && isEsim)}
						tooltip={
							dateReplacedToday && isEsim
								? translate('numbers.settings.replace.esim.card.unavailable')
								: undefined
						}
						simManagementUnavailable={!enableSIManagement}
					/>
					<AuthConsumer>
						{({ user }) => (
							<ReplaceESimCardDialog
								isOpen={showDialog === 'esimCardDialog'}
								hideDialog={hideDialog}
								showDialog={replaceESimCard}
								phoneNumber={phoneNumber}
								accountDetails={accountDetails}
								isEsim={!!isEsim}
								reload={reloadAfterESimReplacement}
								msisdn={user.contactNumber1}
							/>
						)}
					</AuthConsumer>
				</GridItem>
			)}
			<GridItem xs={12} lg={4}>
				<NumberSettingsCard
					text={translate('numbers.settings.check.sim.details')}
					image={SimPin}
					onClick={checkPinPuk}
					notAvailable={!customerAccount}
				/>
				<CheckPinPukDialog
					isOpen={showDialog === 'checkPinPukDialog'}
					hideDialog={hideDialog}
					phoneNumber={phoneNumber}
					customerAccount={customerAccount}
					accountDetails={accountDetails}
					resourceInfo={resourceInfo}
					isEsim={isEsim}
				/>
			</GridItem>

			<ConditionalRender
				show={isPostpaid}
				onTrue={() => (
					<GridItem xs={12} lg={4}>
						<NumberSettingsCard
							text={translate('numbers.settings.data.sim.details')}
							image={DataSim}
							onClick={addDataSim}
							notAvailable={!customerAccount}
							simManagementUnavailable={!enableSIManagement}
						/>
						<ConditionalRender
							show={showDialog === 'dataSimDialog'}
							onTrue={() => (
								<SimDialog
									isOpen={showDialog === 'dataSimDialog'}
									simType={SimType.DATA_SIM}
									hideDialog={hideDialog}
									phoneNumber={phoneNumber}
									accountDetails={accountDetails}
									simsDetails={[
										customerAccount?.product?.firstDataSim,
										customerAccount?.product?.secondDataSim,
									].filter((singleElement) => !!singleElement?.serialNo)}
									headerName={translate('numbers.settings.data.sim.details')}
									maxSimCards={MAX_NUMBER_OF_DATA_SIM_CARDS}
									reload={reload}
								/>
							)}
						/>
					</GridItem>
				)}
			/>

			<ConditionalRender
				show={isPostpaid}
				onTrue={() => (
					<GridItem xs={12} lg={4}>
						<NumberSettingsCard
							text={translate('numbers.settings.sawa.sim.details')}
							image={SawaSim}
							onClick={addSawaSim}
							notAvailable={!customerAccount}
							simManagementUnavailable={!enableSIManagement}
						/>
						<ConditionalRender
							show={showDialog === 'sawaSimDialog'}
							onTrue={() => (
								<SimDialog
									isOpen={showDialog === 'sawaSimDialog'}
									simType={SimType.SAWA_SIM}
									hideDialog={hideDialog}
									phoneNumber={phoneNumber}
									accountDetails={accountDetails}
									simsDetails={[customerAccount?.product?.sawaSim].filter(
										(singleElement) => !!singleElement?.serialNo
									)}
									headerName={translate('numbers.settings.sawa.sim.details')}
									maxSimCards={MAX_NUMBER_OF_SAWA_SIM_CARDS}
									reload={reload}
								/>
							)}
						/>
					</GridItem>
				)}
			/>

			<ConditionalRender
				show={loading}
				onTrue={() => (
					<div className={classes.loaderWrapper}>
						<CustomCircularProgress text={translate('loading')} loading transparent />
					</div>
				)}
			/>
			<ConditionalRender
				show={!customerAccount}
				onTrue={() => (
					<GridItem xs={12} className={classes.tryAgainSection}>
						<ReloadPage text={translate('numbers.settings.try.again')} reload={reload} />
					</GridItem>
				)}
			/>
		</GridContainer>
	);
};

export default SimManagement;
